import { Link } from "react-router-dom";
import { ShieldCheck, Award, HeartHandshake, MapPin } from "lucide-react";
import { site, images, addressLine } from "@/config/site";
import { SEO, breadcrumbLd } from "@/lib/seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTA } from "@/components/CTA";

const values = [
  { icon: ShieldCheck, title: "Fully insured", text: "Every project we take on is covered, from a single room to a full commercial space." },
  { icon: Award, title: `${site.yearsExperience} years of experience`, text: "Seven years of painting homes and businesses across the South Fraser region." },
  { icon: HeartHandshake, title: "Honest by default", text: "Written estimates, realistic timelines and no inflated promises. If something isn't worth doing, we say so." },
  { icon: MapPin, title: "Local to Delta", text: "Based in North Delta and proud to work in the neighbourhoods we live alongside." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Us | North Ember Painting — Delta, BC"
        description="Meet North Ember Painting and owner Ajmal Faiz Mohammad: a fully insured Delta, BC painting company with 7 years of experience in residential and commercial painting."
        path="/about"
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "About Us", path: "/about" }])]}
      />
      <PageHero
        eyebrow="About us"
        title="A local painting company that sweats the details"
        intro="North Ember Painting is a Delta, BC painting company founded on a simple idea: careful preparation and honest communication produce finishes people love living with."
        image="painterRolling"
      />

      <section className="py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ember">Our story</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Built one well-painted room at a time
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-600">
              <p>
                North Ember Painting was founded by <strong className="font-semibold text-slate-900">Ajmal Faiz Mohammad</strong>,
                who has spent {site.yearsExperience} years painting homes and businesses across Delta and the
                surrounding communities. What began as a craft learned on the job became a company built around
                doing that craft properly — the preparation, the protection of your home, and the finish itself.
              </p>
              <p>
                The name says something about how we work. An ember is steady, warm and dependable — not a flash.
                We'd rather be the crew that shows up when promised, protects your floors like they're our own,
                and hands over a room you're genuinely happy with than the biggest painting company in BC.
              </p>
              <p>
                We're a small local business, and we intend to stay honest like one. Our estimates are written
                and specific. Our timelines are realistic. And if your project needs something we don't do, we'll
                tell you — and point you toward someone who does.
              </p>
            </div>
            <dl className="mt-8 grid grid-cols-2 gap-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-widest text-slate-400">Owner</dt>
                <dd className="mt-1 font-display text-base font-semibold text-slate-900">{site.owner}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-widest text-slate-400">Experience</dt>
                <dd className="mt-1 font-display text-base font-semibold text-slate-900">{site.yearsExperience} years</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-widest text-slate-400">Coverage</dt>
                <dd className="mt-1 font-display text-base font-semibold text-slate-900">Fully insured</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-widest text-slate-400">Based in</dt>
                <dd className="mt-1 font-display text-base font-semibold text-slate-900">Delta, BC</dd>
              </div>
            </dl>
          </Reveal>
          <Reveal delay={120}>
            <div className="space-y-6">
              <div className="tilt-card overflow-hidden rounded-2xl shadow-xl">
                <img src={images.painterRolling.url} alt={images.painterRolling.url && images.painterRolling.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <p className="bg-white px-4 py-2.5 text-xs text-slate-500">Illustrative example — not a photo of our team.</p>
              </div>
              <div className="tilt-card overflow-hidden rounded-2xl shadow-xl">
                <img src={images.trimMolding.url} alt={images.trimMolding.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <p className="bg-white px-4 py-2.5 text-xs text-slate-500">Concept image — the standard of finish we aim for.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-100 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              What you can count on
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 70}>
                <div className="tilt-card h-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <v.icon className="h-6 w-6 text-ember" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <p className="max-w-3xl text-sm leading-relaxed text-slate-500">
              North Ember Painting · {site.showAddress ? addressLine : "Delta, BC"} · {site.hours}
            </p>
          </Reveal>
        </div>
      </section>

      <CTA title="Let's talk about your project" text={`Call or message any time — or send the estimate form and we'll review the details. Serving Delta, Ladner, Tsawwassen and Surrey.`} />
    </>
  );
}
